import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { Error404PageWithTranslations } from '@components/Error/Error404PageWithTranslations';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerLoginModal } from '@components/Login/CustomerLoginModal';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton } from 'noddi-ui-web';
import { customerRoutes, hasErrorCode, isAxiosError } from 'noddi-util';
import { CampaignCustomerView, useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ActivateCampaignFromCode } from '../../../components/Campaigns/ActivateCampaignFromCode';
import { ActivateNafCampaignFromCode } from '../../../components/Campaigns/ActivateNafCampaignFromCode';
import { CampaignClaimBottomBar } from '../../../components/Campaigns/CampaignClaimBottomBar';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { buildCampaignClaimDeadline, buildCampaignCustomerDiscounts } from '../../../utils/campaignCustomerDisplay';
import { getCommonTranslations } from '../../../utils/commonTranslations';

export const CampaignFromCode = () => {
  const params = useParams();
  const code = params.code!;
  const { isLoggedIn, canActivateCouponsForNewUsersOnly } = useAuthContext();
  const { currentBrand } = useCurrentBrand();
  const [openLoginModal, setOpenLoginModal] = useState(false);

  const {
    data: campaign,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getCampaignFromCode,
    input: { code }
  });

  if (isPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (error && hasErrorCode(error, 'campaign_no_longer_available')) {
    return (
      <ContentWrapperCustomerApp hideBackButton title={t`Campaign unavailable`}>
        <p className='text-muted-foreground'>
          <Trans>This campaign is no longer available.</Trans>
        </p>
      </ContentWrapperCustomerApp>
    );
  }

  // Old leftover referral links used `/campaigns/:slug` — send those to `/referrals/:slug`.
  if (error && isAxiosError(error) && error.response?.status === 404) {
    return <Navigate replace to={customerRoutes.referralsInfo.getPath({ slug: code })} />;
  }

  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  // A campaign is claimed only on its owner brand's site; another brand's site does not have it.
  if (campaign.brandDomain !== currentBrand) {
    return <Error404PageWithTranslations />;
  }

  const canNotActivateCoupons = isLoggedIn && campaign.isForNewUserOnly && !canActivateCouponsForNewUsersOnly;
  const commonTranslations = getCommonTranslations();
  const requiresNafVerification = campaign.requiresNafVerification;
  const claimDeadline = buildCampaignClaimDeadline(campaign.expiresAt);
  const isClaimExpired = claimDeadline?.isExpired ?? false;

  const claimAction = isClaimExpired ? null : requiresNafVerification ? (
    <ActivateNafCampaignFromCode campaign={campaign} canNotActivateCoupons={canNotActivateCoupons} />
  ) : isLoggedIn ? (
    <ActivateCampaignFromCode campaign={campaign} canNotActivateCoupons={canNotActivateCoupons} />
  ) : (
    <CampaignClaimBottomBar>
      <NoddiButton className='w-full md:w-fit' onClick={() => setOpenLoginModal(true)}>
        <Trans>Create account to get discount</Trans>
      </NoddiButton>
    </CampaignClaimBottomBar>
  );

  return (
    <ContentWrapperCustomerApp hideBackButton>
      <CampaignCustomerView
        title={campaign.name}
        description={campaign.description}
        imageUrl={campaign.image ?? undefined}
        claimDeadline={claimDeadline}
        validAtLabel={t`Valid at`}
        discounts={buildCampaignCustomerDiscounts(campaign)}
        action={claimAction}
        translations={{
          activated: commonTranslations.activated,
          used: commonTranslations.used,
          amount: commonTranslations.amount,
          discount: commonTranslations.discount,
          free: commonTranslations.free
        }}
      />

      <CustomerLoginModal
        onClose={() => setOpenLoginModal(false)}
        open={openLoginModal}
        title={t`To activate your benefits, we first need to verify your phone number`}
        description={t`We will create a user for you, if you do not have one`}
      />
    </ContentWrapperCustomerApp>
  );
};
